import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import Processor, { GenerateStrategy } from 'api/Retomagic'
import Container from 'components/common/Container'
import IdleStage from 'components/stages/generation/IdleStage'
import ProcessingStage from 'components/stages/generation/ProcessingStage'
import FinishStage from 'components/stages/generation/FinishStage'
import MainLayoutContext from 'contexts/MainLayoutContext'
import {
  ProcessedAssetsChangedPayload,
  ProcessorEvents,
} from 'types/ProcessorEvents'
import c from './GenerationPage.module.scss'

function GenerationPage() {
  const {
    stage,
    setStage,
    processedAssets,
    setProcessedAssets,
    currentAssetIndex,
    setCurrentAssetIndex,
  } = useContext(MainLayoutContext)

  const [text, setText] = useState<string>('')

  const currentAssetUrl = useMemo(() => {
    const currentAsset = processedAssets[currentAssetIndex]
    return currentAsset?.url
  }, [processedAssets, currentAssetIndex])

  const handleProcessedAssetsChanged = useCallback(
    (payload: ProcessedAssetsChangedPayload) => {
      const { assets, isFirstSucceededResult } = payload

      setProcessedAssets(assets)

      if (isFirstSucceededResult) {
        setStage('finish')
      }
    },
    [],
  )

  const handleProcessingStart = useCallback(async () => {
    setStage('processing')

    const strategy = new GenerateStrategy(text)

    const processor = new Processor(strategy)
    processor.on(
      ProcessorEvents.PROCESSED_ASSETS_CHANGED,
      handleProcessedAssetsChanged,
    )

    await processor.start()
  }, [text, handleProcessedAssetsChanged])

  useEffect(() => {
    // Сброс данных, если пользователь вернулся на главный экран
    if (stage !== 'idle') return
    setText('')
    setProcessedAssets([])
    setCurrentAssetIndex(0)
  }, [stage])

  useEffect(() => {
    // Изменение допустимых touch-action
    document.documentElement.style.setProperty('--touch-action', 'none')

    return () => {
      document.documentElement.style.setProperty('--touch-action', 'pan-y')
    }
  }, [])

  return (
    <div className={c.main}>
      <Container flex fullHeight fluid={stage === 'finish'}>
        {stage === 'idle' && (
          <IdleStage
            text={text}
            onTextChange={setText}
            onProcessingStart={handleProcessingStart}
          />
        )}
        {stage === 'processing' && <ProcessingStage />}
        {stage === 'finish' && (
          <FinishStage
            text={text}
            currentAssetUrl={currentAssetUrl}
            assets={processedAssets}
            onCurrentAssetIndexChange={setCurrentAssetIndex}
            onGenerateMore={handleProcessingStart}
          />
        )}
      </Container>
    </div>
  )
}

export default GenerationPage
